import axios from "axios";
import { DEFAULT_TIMEOUT_MS, TIMEOUTS, TIMEOUT_CLASSES,
         TOKEN_SOURCES, TOKEN_SOURCE_NAMES } from "./apiConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// v200.52 (2026-08-14) — Timeout is set here (from apiConfig) and here
// only. Every request also carries its own AbortController: on timeout
// we call `controller.abort()` so the underlying fetch/XHR connection
// is really torn down — not just the promise rejected. See guards
// `test_network_layer_singleton.py::test_G1..G4`.
//
// withCredentials lets the browser attach the httpOnly tw_token cookie set by /auth/login.
// During the migration window, we still also send the Authorization header from any
// pre-existing localStorage token so logged-in users aren't kicked out at deploy time.
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: DEFAULT_TIMEOUT_MS,
});

// Per-request: attach an AbortController if the caller didn't. When
// axios times out (`ECONNABORTED`), we invoke `controller.abort()`
// through the response interceptor so the underlying connection is
// really cancelled — Chrome/Safari otherwise keep the socket alive
// until server response, which is exactly the hang we're eliminating.
// D2 — «فترة اليوم» على صفحة المبيعات: مصدرها رابط الصفحة (?tab=pos&sub=sales&daypart=) فتُمرَّر لكل طلبات
// الصفحة (/pos/* و/customers/unified-list) ويطبّق الخادم القيد نفسه على دواله القائمة. خارج الصفحة لا أثر لها.
const DAYPART_PATHS = /^\/(pos\/|customers\/unified-list)/;
api.interceptors.request.use((config) => {
  const q = new URLSearchParams(window.location.search);
  const dp = q.get("tab") === "pos" && q.get("sub") === "sales" ? q.get("daypart") : null;
  if (dp && dp !== "all" && DAYPART_PATHS.test(config.url || "") && !/[?&]daypart=/.test(config.url || "")
      && !(config.params && config.params.daypart)) {
    config.params = { ...(config.params || {}), daypart: dp };
  }
  return config;
});
api.interceptors.request.use((config) => {
  // v200.54 — resolve token source. المصدر الافتراضي MAIN. مصدر غير
  // معروف يرفع خطأ فورياً — نفس نمط G5 على مستوى وقت التشغيل.
  const src = config.tokenSource || "MAIN";
  if (!TOKEN_SOURCE_NAMES.includes(src)) {
    throw new Error(
      `[api] tokenSource="${src}" غير معروف. المصادر المسموحة: ` +
      TOKEN_SOURCE_NAMES.join(", ")
    );
  }
  const storageKey = TOKEN_SOURCES[src].storageKey;
  const token = storageKey
    ? (sessionStorage.getItem(storageKey) || localStorage.getItem(storageKey))
    : null;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (config.tokenSource) delete config.tokenSource;
  // CSRF: read the non-httpOnly `tw_csrf` cookie and echo it on every state-changing call.
  // Backend's CSRFMiddleware validates this against the cookie copy.
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const csrf = readCookie("tw_csrf");
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  // v200.53 — أي نداء يمرّ عبر النسخة الواحدة يُصرّح صنف مهلته
  // في `config.timeoutClass` من قائمة `TIMEOUT_CLASSES`. الصنف
  // يُترجَم هنا إلى `config.timeout` (بالمللي‑ثانية)، ثم يُنزَع
  // المفتاح قبل تسليم الطلب لـ axios. غياب `timeoutClass` مسموح
  // فقط عندما يقبل المستدعي «QUICK_READ» ضمناً — الحارس G5
  // يفرض التصريح الصريح، وينمر على الاستدعاءات بلا صنف.
  if (config.timeoutClass) {
    const cls = config.timeoutClass;
    if (!TIMEOUT_CLASSES.includes(cls)) {
      throw new Error(
        `[api] timeoutClass="${cls}" غير معروف. الأصناف المسموحة: ` +
        TIMEOUT_CLASSES.join(", ")
      );
    }
    config.timeout = TIMEOUTS[cls];
    delete config.timeoutClass;
  }
  // v200.52 — attach a per-request AbortController when the caller
  // did not supply their own signal. Stash it on config so the
  // response interceptor can fire abort() on timeout.
  if (!config.signal) {
    const ac = new AbortController();
    config.signal = ac.signal;
    config.__abortController = ac;
  }
  return config;
});

/**
 * v200.52 — makeRequestController()
 * Helper for React components: create a controller, pass its
 * `.signal` to `api.get(url, { signal })`, and call
 * `.cancel()` from a cleanup effect on unmount. Cancelling triggers
 * a real network abort — not just a swallowed promise.
 *
 * Usage:
 *   useEffect(() => {
 *     const c = makeRequestController();
 *     api.get("/customers/directory", { signal: c.signal })
 *        .then(r => setData(r.data)).catch(() => {});
 *     return () => c.cancel();
 *   }, []);
 */
export function makeRequestController() {
  const ac = new AbortController();
  return {
    signal: ac.signal,
    cancel: () => { try { ac.abort(); } catch (_) { /* noop */ } },
  };
}

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : "";
}

let _refreshPromise = null;

// R — «تذكرني»: رمز التحديث الطويل في localStorage (يتجدد مع الاستخدام)؛ بدونها sessionStorage كما كان.
export const LONG_REFRESH_KEY = "tw_refresh_long";

export async function tryRefresh() {
  // Coalesce concurrent refresh attempts
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const refresh = sessionStorage.getItem("tw_refresh") || localStorage.getItem(LONG_REFRESH_KEY) || "";
      const body = refresh ? { refresh } : {};
      const { data } = await axios.post(`${API}/auth/refresh`, body, { withCredentials: true });
      if (data?.token) {
        sessionStorage.setItem("tw_token", data.token);
        if (data?.user) sessionStorage.setItem("tw_user", JSON.stringify(data.user));
      }
      if (data?.refresh && localStorage.getItem(LONG_REFRESH_KEY)) localStorage.setItem(LONG_REFRESH_KEY, data.refresh);
      return data?.token || null;
    } catch (_) {
      return null;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    // v-safety — نقطة واحدة: طبّع مصفوفة `detail` (خطأ FastAPI 422) إلى نص
    // قبل أن تصل أي شاشة، حتى لا يعرض أي setError(detail) مصفوفةً كـJSX child
    // (وهو سبب الشاشة البيضاء الصامتة). الكائنات تُترك كما هي حفاظًا على من
    // يقرأ detail.<field> (مثل POSBackfillPanel.jsx:154 → detail?.message).
    try {
      const _d = err?.response?.data?.detail;
      if (Array.isArray(_d)) {
        err.response.data.detail = _d
          .map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x)))
          .filter(Boolean)
          .join(" · ");
      }
    } catch (_e) { /* noop */ }
    // v200.52 — on axios timeout (`ECONNABORTED` + `err.code === "ECONNABORTED"`)
    // fire abort() on the attached controller so the browser really
    // cancels the connection. Then rewrite the message in Arabic so
    // the surface never labels this as "Network Error".
    if (err && (err.code === "ECONNABORTED" || /timeout/i.test(err.message || ""))) {
      try { err.config?.__abortController?.abort(); } catch (_) { /* noop */ }
      const ms = err.config?.timeout || DEFAULT_TIMEOUT_MS;
      err.message = `انتهت مهلة الطلب بعد ${ms} مللي‑ثانية — أُلغي الاتصال. حاول لاحقاً.`;
      err.isTimeout = true;
    }
    const url = err.config?.url || "";
    const isAuthRoute =
      url.includes("/auth/me") ||
      url.includes("/auth/logout") ||
      url.includes("/auth/login") ||
      url.includes("/auth/refresh");

    // Try silent refresh on 401 (except for auth routes themselves)
    if (err.response?.status === 401 && !isAuthRoute && !err.config?._retried) {
      const newToken = await tryRefresh();
      if (newToken) {
        err.config._retried = true;
        err.config.headers.Authorization = `Bearer ${newToken}`;
        return api.request(err.config);
      }
      // Refresh failed → force re-login
      sessionStorage.removeItem("tw_token");
      sessionStorage.removeItem("tw_refresh");
      sessionStorage.removeItem("tw_user");
      localStorage.removeItem(LONG_REFRESH_KEY);
      localStorage.removeItem("tw_token");
      localStorage.removeItem("tw_user");
      if (!window.location.pathname.includes("login")) {
        window.dispatchEvent(new CustomEvent("auth:logout"));
      }
    }
    return Promise.reject(err);
  }
);

export function formatErr(err) {
  // Accept either a raw detail value OR a full AxiosError; unwrap to detail.
  let detail = err;
  if (err && typeof err === "object" && "isAxiosError" in err) {
    detail = err.response?.data?.detail ?? err.response?.data?.message ?? err.message;
  } else if (err && typeof err === "object" && err.response) {
    detail = err.response?.data?.detail ?? err.response?.data?.message ?? err.message;
  }
  if (detail == null) return "حدث خطأ ما، حاول مرة أخرى";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
